import { type CSSProperties, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBuilderActions } from '@customerio/dryad-react';
import { Box, Button, Icon, InlineStack, PlainButton, Stack, TextLabel } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import plur from 'plur';
import { safeQueryDecode } from '@/utils/condition-query-encoding';
import { isRecord } from '@/utils/objects';
import { collectionFilterBuilder } from './builder';
import { CollectionAttributeRow } from './collection-attribute-row';
import {
  COLLECTION_ATTRIBUTE_CONDITION_TYPE,
  FLAT_FILTER_FORMAT,
  attributeConditionOperatorGroups,
  isCommittableCollectionAttributeFilter,
  isRelativeTimestampOperator,
  isTwoValueFilterOperator,
  isUnaryFilterOperator,
  isValidAttributeFilterState,
  normalizeTimestampOperatorForSelect,
  relativeTimestampDays,
  relativeTimestampDirection,
  type AttributeFilterState,
} from './dryad-utils';

collectionFilterBuilder.registerView(COLLECTION_ATTRIBUTE_CONDITION_TYPE, CollectionAttributeRow);

type CollectionFilterBuilderProps = {
  conditions: string | null;
  disabled?: boolean;
  schema: string[];
  onChange: (conditions: unknown) => void;
};

type CollectionFilterRowsProps = {
  draftIds: Set<string>;
  editingIds: Set<string>;
  onAddDraftId: (id: string) => void;
  onAddEditingId: (id: string) => void;
  onApply: (appliedId?: string) => void;
  onRemoveDraftId: (id: string) => void;
  onEdit: (id: string) => void;
  onRemoveEditingId: (id: string) => void;
};

type CollectionFilterRowProps = {
  id: string;
  isEditing: boolean;
  onEdit: () => void;
  onRemove: () => void;
  showLogicalOperator: boolean;
  onApply: () => void;
};

type ConditionValueLabelProps = {
  value: string | null;
};

type CollectionFilterLabelProps = {
  id: string;
  onEdit: () => void;
};

type DataSourceLabelProps = {
  children: string;
  className?: string;
  icon?: 'person';
};

const logicalOperatorLabelStyle = {
  display: 'inline-block',
  padding: `${themeVars.space['050']} ${themeVars.space['075']}`,
  textTransform: 'uppercase',
  fontSize: '0.625rem',
  fontWeight: themeVars['font-weight'].bold,
  color: themeVars.color['text-subtle'],
  backgroundColor: themeVars.color['surface-base'],
  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
  borderRadius: themeVars.radius['4'],
  lineHeight: 1.5,
} satisfies CSSProperties;

const conditionLabelButtonStyle = {
  padding: 0,
  border: 0,
  backgroundColor: 'transparent',
  color: themeVars.color['text-base'],
  minWidth: 0,
  maxWidth: '100%',
  textAlign: 'left',
} satisfies CSSProperties;

const dataSourceLabelStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: themeVars.space['025'],
  minWidth: 0,
  maxWidth: '100%',
  padding: `${themeVars.space['075']} ${themeVars.space['150']}`,
  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
  borderRadius: themeVars.radius['4'],
  backgroundColor: 'transparent',
  color: themeVars.color['text-base'],
  fontSize: themeVars['font-size'].s,
  fontWeight: themeVars['font-weight'].medium,
  lineHeight: 'initial',
  minHeight: '30px',
  overflowWrap: 'anywhere',
  verticalAlign: 'baseline',
  wordBreak: 'break-all',
} satisfies CSSProperties;

function LogicalOperatorLabel() {
  return (
    <Box as="span" style={logicalOperatorLabelStyle} data-testid="collection-filter-logical-operator">
      and
    </Box>
  );
}

function operatorLabel(operator: string): string {
  const normalizedOperator = normalizeTimestampOperatorForSelect(operator);

  return (
    attributeConditionOperatorGroups
      .flatMap((group) => group.options)
      .find((option) => option.value === normalizedOperator)?.label ?? operator
  );
}

function ConditionValueLabel({ value }: ConditionValueLabelProps) {
  return (
    <DataSourceLabel className={value == null || value === '' ? undefined : 'fs-mask'}>
      {value == null || value === '' ? 'Blank' : value}
    </DataSourceLabel>
  );
}

function relativeTimestampLabel(state: AttributeFilterState): string {
  const days = relativeTimestampDays(state.value);
  return `${days} ${plur('day', days)} ${relativeTimestampDirection(state.value)}`;
}

function conditionValueLabel(state: AttributeFilterState): string | null {
  return isRelativeTimestampOperator(state.operator) ? relativeTimestampLabel(state) : state.value;
}

function DataSourceLabel({ children, className, icon }: DataSourceLabelProps) {
  return (
    <Box as="span" className={className} style={dataSourceLabelStyle}>
      {icon != null ? <Icon name={icon} color="inherit" size="sm" /> : null}
      <span>{children}</span>
    </Box>
  );
}

function CollectionFilterLabel({ id, onEdit }: CollectionFilterLabelProps) {
  const { state: filterState } = collectionFilterBuilder.useLeafState(id, COLLECTION_ATTRIBUTE_CONDITION_TYPE);

  return (
    <PlainButton
      aria-label={`Edit filter ${filterState.field}`}
      display="inline-flex"
      style={conditionLabelButtonStyle}
      data-testid="collection-filter-condition-view"
      onClick={onEdit}
    >
      <InlineStack as="span" alignBlock="center" gap="050" wrap>
        <DataSourceLabel icon="person">{filterState.field}</DataSourceLabel>
        <span>{operatorLabel(filterState.operator)}</span>
        {!isUnaryFilterOperator(filterState.operator) ? (
          <ConditionValueLabel value={conditionValueLabel(filterState)} />
        ) : null}
        {isTwoValueFilterOperator(filterState.operator) ? (
          <>
            <span>and</span>
            <ConditionValueLabel value={filterState.value2} />
          </>
        ) : null}
      </InlineStack>
    </PlainButton>
  );
}

function CollectionFilterRow({
  id,
  isEditing,
  onEdit,
  onRemove,
  showLogicalOperator,
  onApply,
}: CollectionFilterRowProps) {
  const meta = collectionFilterBuilder.useMeta();
  const View = collectionFilterBuilder.useViewForNode(COLLECTION_ATTRIBUTE_CONDITION_TYPE);
  const { state: filterState } = collectionFilterBuilder.useLeafState(id, COLLECTION_ATTRIBUTE_CONDITION_TYPE);
  const isApplyDisabled = meta.isDisabled === true || !isValidAttributeFilterState(filterState);

  if (View == null) return null;

  return (
    <InlineStack alignBlock="center" gap="100" wrap>
      {showLogicalOperator ? <LogicalOperatorLabel /> : null}
      {isEditing ? (
        <InlineStack alignBlock="center" gap="100" wrap>
          <View id={id} />
          <InlineStack alignBlock="center" gap="100">
            <Button
              aria-label="Apply filter"
              icon="check"
              isIconOnly={true}
              size="sm"
              variant="primary"
              onClick={onApply}
              isDisabled={isApplyDisabled}
            />
            <Button
              aria-label="Remove filter"
              icon="close"
              isIconOnly={true}
              size="sm"
              variant="subtle"
              isDanger={true}
              onClick={onRemove}
              isDisabled={meta.isDisabled}
            />
          </InlineStack>
        </InlineStack>
      ) : (
        <InlineStack alignBlock="center" gap="100">
          <CollectionFilterLabel id={id} onEdit={onEdit} />
          <Button
            aria-label="Remove filter"
            icon="close"
            isIconOnly={true}
            size="sm"
            variant="subtle"
            isDanger={true}
            onClick={onRemove}
            isDisabled={meta.isDisabled}
          />
        </InlineStack>
      )}
    </InlineStack>
  );
}

function CollectionFilterRows({
  draftIds,
  editingIds,
  onAddDraftId,
  onAddEditingId,
  onApply,
  onRemoveDraftId,
  onEdit,
  onRemoveEditingId,
}: CollectionFilterRowsProps) {
  // Subscribed read: an external filters change replaces the tree, so the root
  // this renders against has to be re-read rather than captured on mount.
  const tree = collectionFilterBuilder.useTree();
  const { addNode, removeNode } = useBuilderActions();
  const meta = collectionFilterBuilder.useMeta();
  const output = collectionFilterBuilder.useOutput(FLAT_FILTER_FORMAT);
  const leafChildren = tree.children.filter((child) => child.kind === 'leaf');
  const hasActiveEditor = editingIds.size > 0;

  const addFilter = () => {
    const id = addNode(tree.id, COLLECTION_ATTRIBUTE_CONDITION_TYPE);
    if (id != null) {
      onAddDraftId(id);
      onAddEditingId(id);
    }
  };

  return (
    <Stack gap="100">
      {leafChildren.map((child, index) => (
        <CollectionFilterRow
          key={child.id}
          id={child.id}
          isEditing={editingIds.has(child.id)}
          onEdit={() => onEdit(child.id)}
          onRemove={() => {
            removeNode(child.id);
            onRemoveEditingId(child.id);
            onRemoveDraftId(child.id);
            if (!output.isValid) {
              for (const editingId of editingIds) {
                if (editingId !== child.id && draftIds.has(editingId)) {
                  removeNode(editingId);
                  onRemoveEditingId(editingId);
                  onRemoveDraftId(editingId);
                }
              }
            }
            onApply();
          }}
          showLogicalOperator={index > 0}
          onApply={() => {
            onApply(child.id);
            onRemoveEditingId(child.id);
            onRemoveDraftId(child.id);
          }}
        />
      ))}
      {!hasActiveEditor ? (
        <InlineStack alignBlock="center" gap="100" wrap>
          {leafChildren.length > 0 ? <LogicalOperatorLabel /> : null}
          <Button size="sm" variant="tertiary" onClick={addFilter} isDisabled={meta.isDisabled}>
            Add query condition
          </Button>
        </InlineStack>
      ) : null}
    </Stack>
  );
}

function decodedConditions(conditions: string | null): unknown {
  return conditions == null ? [] : safeQueryDecode(conditions);
}

function topLevelConditions(conditions: unknown): unknown[] {
  if (Array.isArray(conditions)) {
    return conditions;
  }
  if (isRecord(conditions) && Array.isArray(conditions.and)) {
    return conditions.and;
  }
  return [];
}

function committedConditionsForBuilder(
  builder: ReturnType<typeof collectionFilterBuilder.useConditionBuilder>,
  initialConditions: unknown,
): Map<string, unknown> {
  const committedConditions = topLevelConditions(initialConditions);
  const committedConditionsById = new Map<string, unknown>();

  // Every child, not just the leaves: serialization emits one entry per child
  // and an unrenderable condition parses to an error node, so filtering here
  // would slide the pairing by one for the rest of the tree.
  builder.getTree().children.forEach((child, index) => {
    if (index < committedConditions.length) {
      committedConditionsById.set(child.id, committedConditions[index]);
    }
  });

  return committedConditionsById;
}

function sanitizedConditions(
  builder: ReturnType<typeof collectionFilterBuilder.useConditionBuilder>,
  conditions: unknown,
  committedConditionsById: Map<string, unknown>,
  draftIds: Set<string>,
  editingIds: Set<string>,
  appliedId?: string,
): unknown {
  if (!Array.isArray(conditions)) {
    return conditions;
  }

  const children = builder.getTree().children;

  return conditions.flatMap((condition, index) => {
    const child = children[index];
    if (child == null) {
      return [condition];
    }
    if (child.id === appliedId) {
      return [condition];
    }
    if (draftIds.has(child.id)) {
      return [];
    }
    if (editingIds.has(child.id)) {
      const committedCondition = committedConditionsById.get(child.id);
      return committedCondition == null ? [] : [committedCondition];
    }
    return isCommittableCollectionAttributeFilter(condition)
      ? [condition]
      : [committedConditionsById.get(child.id) ?? condition];
  });
}

function notifyChange(
  builder: ReturnType<typeof collectionFilterBuilder.useConditionBuilder>,
  onChange: CollectionFilterBuilderProps['onChange'],
  committedConditionsById: Map<string, unknown>,
  draftIds: Set<string>,
  editingIds: Set<string>,
  appliedId?: string,
) {
  const { serialized } = builder.sources.output(FLAT_FILTER_FORMAT).get();

  onChange(sanitizedConditions(builder, serialized, committedConditionsById, draftIds, editingIds, appliedId));
}

export function CollectionFilterBuilder({ conditions, disabled, schema, onChange }: CollectionFilterBuilderProps) {
  const [draftIds, setDraftIds] = useState<Set<string>>(new Set());
  const [editingIds, setEditingIds] = useState<Set<string>>(new Set());
  const initialConditions = useMemo(() => decodedConditions(conditions), [conditions]);
  const builder = collectionFilterBuilder.useConditionBuilder({
    initialValue: initialConditions,
    format: FLAT_FILTER_FORMAT,
  });

  const committedConditionsById = useRef<Map<string, unknown>>(new Map());
  // Runs after dryad's own pre-paint value sync, so the tree already matches
  // the incoming conditions and the id-to-committed-value pairing lines up.
  useLayoutEffect(() => {
    committedConditionsById.current = committedConditionsForBuilder(builder, initialConditions);
    setDraftIds((current) => (current.size === 0 ? current : new Set()));
    setEditingIds((current) => (current.size === 0 ? current : new Set()));
  }, [builder, initialConditions]);

  const addDraftId = (id: string) => setDraftIds((current) => new Set(current).add(id));
  const removeDraftId = (id: string) =>
    setDraftIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  const addEditingId = (id: string) => setEditingIds((current) => new Set(current).add(id));
  const removeEditingId = (id: string) =>
    setEditingIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

  return (
    <InlineStack alignBlock="baseline" gap="100" mb="100" mt={conditions == null ? '200' : undefined} wrap>
      <TextLabel>Filter by</TextLabel>
      <collectionFilterBuilder.DryadProvider builder={builder} meta={{ isDisabled: disabled === true, schema }}>
        <CollectionFilterRows
          draftIds={draftIds}
          editingIds={editingIds}
          onAddDraftId={addDraftId}
          onAddEditingId={addEditingId}
          onRemoveDraftId={removeDraftId}
          onEdit={addEditingId}
          onRemoveEditingId={removeEditingId}
          onApply={(appliedId) =>
            notifyChange(builder, onChange, committedConditionsById.current, draftIds, editingIds, appliedId)
          }
        />
      </collectionFilterBuilder.DryadProvider>
    </InlineStack>
  );
}
